
import { WizConstants } from '../../model/constants';
import { FeiVO } from '../../vo/workingdata/fei.vo';
import { AkmFDVCell, IBaseline, IBaselineValue, KeyPair, Triggers } from '../../model/interfaces';
import { WidgetCell, WidgetLine } from '../../vo/workingdata/widget-line.obj';
import { FeiKeyDef } from '../../vo/workingdata/fei-key-definition.obj';
import { Baseline, BaselineLite, BaselineValue, BaselineValueLite, Measure, VisionFilterKey, VisionKey, VisionStep } from '@wizeoo/akm-message-lib';
import { EM_ACTION, FIELD_KIND, KEY_KIND } from '../../model/enums';
import { ChronocodeVO } from '../../vo/core/chronocode.vo';
import { MeasMode } from '../computer/measure.mode.obj';
import { VisionVO } from '../../vo/vision/vision.vo';
import { FreeDataVO } from '../../vo/core/freedata.vo';
import { MeasureCoreEngine } from '../computer/measure.core.engine';
import { WizMathUtils } from '@wizeoo/akm-core-lib';

  // codes des fonctions pour injection depuis IHM
  export enum INJECTION {
    VISION_LABEL = 'VISION_LABEL', // trsLabel sur visionVO
    VISION_DESC = 'VISION_DESC', // trsDescription sur visionVO
    VISION_ICON = 'VISION_ICON', // iconOrDie sur visionVO
    MEASURE_LABEL = 'MEASURE_LABEL', // trsLabel sur MeasureVO
    MEASURE_DESC = 'MEASURE_DESC', // trsDescription sur MeasureVO
    MEASURE_CONTROL_MESSAGE = 'MEASURE_CONTROL_MESSAGE', // message de controle de cohérence sur MeasureVO
    MEASURE_EDIT_RESTRICT_MESSAGE = 'MEASURE_EDIT_RESTRICT_MESSAGE', // message de restriction en édition sur MeasureVO
    MEASURE_SELECTED = 'MEASURE_SELECTED', // flag de sélection dans la vue des mesures
    MEASURE_NO_DEPENDENCY = 'MEASURE_NO_DEPENDENCY', // flag mesures de 1er niveau pour la vue des mesures
  }

/**
 * Manager de baselines pour le moteur
 */
export class BaselineEngine {

  /**
   * map de méthodes à surcharger par injection dans les managers
   * code de la fonction, la fonction
   */
  public static injectionMap: Map<string, Function> = new Map();
  
  /**
   * appel d'une fonction injection
   * @param key code de la fonction
   * @param args les params de la fonction
   * @returns 
   */
  public static injectorCall(key: string, args: any): any {
    let result: any = null;
    
    let func: Function = this.injectionMap.get(key);
    if(!!func) {
     result = func(args);
    }
    return result;
  }



   /* ##############################################################################
              utilitaires pour les visions et les steps
    ############################################################################### */ 

  /**
   * avant commit, nettoyage des champs injecté sur la Vision pour obtenir un objet propre
   * @param vision 
   */  
  public static clearVisionVolatileFields(vision: VisionVO): void {
    vision.nbSteps = null;
    // vision.stepKeyByLevel = null;
    /// vision.keySizeByLevel = null;
    vision.measureByLevel = null;
    vision.keyProperties = null;
  }

  /**
   * construction de la map de mesures par niveau de step pour la vision
   * @param vision 
   */
  private static buildMeasureByLevel(vision: VisionVO): void { 
    vision.measureByLevel = new Map();
    // on boucle sur les steps
    Object.values(vision.visionSteps || {}).forEach((vs: VisionStep) => {
      vision.measureByLevel.set(vs.level, new Map());
      // on les tri par level, pour obtenir le bon (le mien)
      let sortedMeasures: Measure[] = vision.measures.sort((a,b) => a.stepLevel - b.stepLevel);
      // console.log('sortedMeasure', sortedMeasures);
      sortedMeasures.forEach((m: Measure) => {
        if(m.stepLevel === 0 || m.stepLevel === vs.level) { // stepLevel 0, les mesures disponibles pour tout le monde
          vision.measureByLevel.get(vs.level).set(m.shortCode, m);
        }
      });
    });
  }

  /**
   * accesseur rapide sur le nombre de step de la vision
   * @param vision 
   * @returns 
   */
  public static getVisionStepCount(vision : VisionVO): number {
    if(!vision.nbSteps) {
      vision.nbSteps = Object.values(vision.visionSteps || {}).length;
    }
    return vision.nbSteps;
  }

  /**
   * retourne les mesures pour le step
   * @param vision 
   * @param visionStep
   * @returns les mesures pour le step
   */
  public static getStepMeasures(vision: VisionVO, visionStep: VisionStep): Measure[] {
    if(!vision.measureByLevel || vision.measureByLevel.size <= 0) { // pas encore construit les mesures par level, on le fait
      this.buildMeasureByLevel(vision);
    }
    return [...vision.measureByLevel.get(visionStep.level).values()];

  }

  /**
   * retourne une mesure précise pour un niveau de step
   * @param vision 
   * @param visionStep 
   * @param shortCode code complet de la mesure
   * @returns 
   */
  public static getStepMeasureByCode(vision: VisionVO, visionStep: VisionStep, shortCode: string): Measure {
    if(!vision.measureByLevel || vision.measureByLevel.size <= 0) { // pas encore construit les mesures par level, on le fait
      this.buildMeasureByLevel(vision);
    }
    return vision.measureByLevel.get(visionStep.level).get(shortCode);

  }

  /**
   * suppression du pool de mesures pour un niveau de step
   * @param vision 
   * @param step 
   */
  // public static clearMeasureByLevel(vision: VisionVO, step: VisionStep): void {
  //   vision.measureByLevel?.delete(step.level);
  // }

  /**
  * retourne le step correspondant au niveau
  * @param level niveau du step
  */
  public static getVisionStepByLevel(vision: VisionVO, level: number): VisionStep {
      return vision.visionSteps ? vision.visionSteps[level]: null;
  }

  /** 
   * accesseur sur les keys du step, uniquement les siennes
   * @param vision IVision
   * @param step cible
   * @returns les visionKey spécifiques à ce step
   */
  public static getStepOnlyKeys(vision: VisionVO, step: VisionStep): VisionKey[] {
    let result: VisionKey[] = [];

    Object.values(vision.visionKeys || {}).forEach((vk: VisionKey) => {
      if(vk.stepLevel === step.level) {
        result.push(vk);
      }
    });
    return result;
  }
      
  /** 
   * accesseur sur les keys du step (toutes, donc celles des niveaux inférieurs)
   * Alimentation de la map sur vision si non présente
   * @param vision IVision
   * @param step cible
   * @returns les VisionKey[] dans le bon ordre
   */
  public static getStepKeys(vision: VisionVO, step: VisionStep): VisionKey[] {
    return vision.stepKeyByLevel?.get(step?.level) || [];
  }

   /* ##############################################################################
            construction et manipulations de clés de FEI
    ############################################################################### */ 

  /**
   * construction d'une FeiKeyDef, les éléments structurant des Feis (les clés)
   * pour alimentation de la KeyMap sur le FEI
   * @param type KEY_KIND
   * @param source Measure | VisionKey | VisionFilterKey
   * @param mode FISTCW en cas de mesure
   * @returns la FeiKeyDef
   */
  public static buildFeiKeyDef(fei: FeiVO, kind: KEY_KIND, source: Measure | VisionKey | VisionFilterKey, mode: string = null): FeiKeyDef {
    let fkd: FeiKeyDef = new FeiKeyDef();
    if (kind === KEY_KIND.MEASURE) {
      let measure: Measure = source as Measure;
      fkd.measure = measure;
      fkd.isMeasure = true;
      fkd.code = measure.shortCode;
      fkd.mode = mode;
      fkd.fullCode = fkd.code + WizConstants.CONTEXT_SEPARATOR + fkd.mode;
      fkd.nbDecimal = measure.nbDecimal;
      fkd.datakind = measure.dataKind || FIELD_KIND.STRING;

      // gestion IHM
      fkd.defaultFormat = measure.formatCode;
      fkd.defaultStyle = measure.styleCode;
      fkd.sortKey = 15; // par défaut, au bout
      if (fkd.mode === WizConstants.MEASURE_MODE_INTERACT) {
        if (measure.editable) { // pour le mode I, soit éditable, soit calculé
          // mesure, on check l'éditable sur I
          fkd.sortKey = 5; // les éditables d'abord
          fkd.editable = true;
        } else {
          fkd.sortKey = 10; // les autres ensuite
          fkd.calculated = true;
        }
      } else {
        if(fkd.mode === WizConstants.MEASURE_MODE_CONSTANT) {
          fkd.sortKey = 12; // les constantes avant le reste
        } 
      }



    } else { // pas une mesure, soit une visionKey soit une filterKey     
      fkd.fullCode = source.code;
      const hierCodeSplit: string[] = source.code.split(WizConstants.CONTEXT_SEPARATOR); // on split la clé pour tester la hiérarchie
      fkd.code = hierCodeSplit[0];
      fkd.level = Number(hierCodeSplit[1]);
      fkd.isChrono = fkd.code === WizConstants.CHRONO; // c'est un Chrono
      fkd.isFreedata = !fkd.isChrono; // sinon c'est un Freedata
      fkd.isKey = true;

      fkd.isScen = fkd.code === WizConstants.SCEN_KEY_CODE; // c'est le scenar, en visionKey

      if(fkd.isChrono) { // on force date sur les chronos
        fkd.datakind = FIELD_KIND.DATE;
      }

      if(kind === KEY_KIND.SCEN) {
        fkd.visionKey = source as VisionKey;
        fkd.sortKey = 1; // les clés de vision en premier
        fkd.datakind = FIELD_KIND.STRING;
      }
      else if (kind === KEY_KIND.KEY) {
        fkd.visionKey = source as VisionKey;
        fkd.sortKey = 1; // les clés de vision en premier
        fkd.indexInKey = fkd.visionKey.order - 1;
        fkd.datakind = FIELD_KIND.STRING;


        if(fkd.isChrono) {
          fkd.datakind = FIELD_KIND.DATE;
        } else { // un Freedata, on regarde si c'est une séquence
          if(fei) {
            let fd: FreeDataVO = fei.model.freedataEngine.getFreeData(fkd.code);
            if(fd?.kindWild) {
              fkd.isSequence = true;
            }
          }
        }


      } else if (kind === KEY_KIND.FILTER_KEY) {
        fkd.visionFilterKey = source as VisionFilterKey;
        fkd.isFilterKey = true;
        fkd.sortKey = 3; // les filters en 2ème
        fkd.editable = true;
        fkd.datakind = FIELD_KIND.STRING;
      }
    }

    return fkd;
  }

  /**
   * alimentation de la structure de clés (key, filters, measures...)
   * @param fei 
   */
  public static buildKeys(fei: FeiVO): void {
    // console.log('feiEngine buildKeys', fei.uniqueName);
    let visionKeys: FeiKeyDef[] = [];
    let filterKeys: FeiKeyDef[] = [];
    let keys: FeiKeyDef[] = [];
    let editableKeys: FeiKeyDef[] = [];
    let triggersMap: Map<string, Triggers> = null;


    // on collecte les clés de la vision pour le step
    if (fei.vision.visionKeys) {
      
      // on ajoute toujours le scenario
      let scenKey: VisionKey = new VisionKey();
      scenKey.code= WizConstants.SCEN_KEY_CODE;
      scenKey.level = 1;
      scenKey.visionCode = fei.visionCode;
      let scenPKD: FeiKeyDef = this.buildFeiKeyDef(fei, KEY_KIND.SCEN, scenKey).setParams(false, true);
      keys.push(scenPKD);
 
      // on ajoute les clés de vision
      Object.values(fei.vision.visionKeys || {})
        .sort((a, b) => a.order - b.order)
        .forEach((item: VisionKey) => {
          if (fei.visionStep?.level >= item.stepLevel) { // il y a un step et le niveau correspond 
            let keydef = this.buildFeiKeyDef(fei, KEY_KIND.KEY, item).setParams(false, true)
            visionKeys.push(keydef);
            keys.push(keydef);
          }
        });
    }

    // on ajoute les filterKeys
    if (fei.vision.visionFilterKeys) {
      Object.values(fei.vision.visionFilterKeys || {})
      .sort((a, b) => a.order - b.order)
      .forEach((item: VisionFilterKey) => {
        let keydef = this.buildFeiKeyDef(fei, KEY_KIND.FILTER_KEY, item).setParams(false, true);
        filterKeys.push(keydef);
        keys.push(keydef);
        editableKeys.push(keydef);
      });
    }

    if(!fei.vision.measureByLevel) { // pas encore construit les mesures par level, on le fait
      this.buildMeasureByLevel(fei.vision);
    }

    if(fei.vision.measureByLevel.has(fei.visionStep.level)) {
      triggersMap = new Map(); // préparation de la map des triggers

      [...fei.vision.measureByLevel.get(fei.visionStep.level).values()].forEach((m: Measure) => {
        if (!!m.mode && m.mode !== WizConstants.MEASURE_MODE_CONSTANT) {
          m.mode.split('').forEach((mode: string) => {
            let keydef = this.buildFeiKeyDef(fei, KEY_KIND.MEASURE, m, mode).setParams(false, true).setSize(150, 20);
            keys.push(keydef);
            if(m.editable && mode === WizConstants.MEASURE_MODE_INTERACT) {
              editableKeys.push(keydef);

              // alimentation de la map des triggers
              if(m.triggerValue) {
                let triggers: Set<string> = new Set();
                let fakeTriggerValueKey: string = [WizConstants.FAKE_TRIGGER, m.shortCode].join(WizConstants.CODE_LEVEL_SEPARATOR);
                const mm: MeasMode = MeasureCoreEngine.LightFakeMeasureMode(
                        fei,
                        fakeTriggerValueKey,
                        WizConstants.MEASURE_MODE_SUBMIT,
                        m.defaultValue);
                
                let trigParts: string[] = m.triggerValue.split(WizConstants.KEY_SEPARATOR);
                trigParts.forEach(t => {
                  triggers.add(t);
                });
                // if(!!mm.deps) {
                //   [...mm.deps.values()].forEach(s => {
                //     triggers.add(s);
                //   })
                // }
                triggersMap.set(fakeTriggerValueKey, { fkd: keydef, defaultValue: m.defaultValue, mm, triggers }); // mémorisation du trigger
              }
            }
          });
        }
      });
    }

    fei.visionKeys = visionKeys; // ordonnées
    fei.filterKeys = filterKeys; // ordonnées
    fei.keys = keys; // on alimente le FEI
    fei.keyMap = new Map(keys.map(key => [key.fullCode, key]));
    fei.editableKeys = new Map(editableKeys.map(key => [key.fullCode, key]));
    fei.triggersMap = triggersMap;
  }

  /** 
   * accesseur composite sur la donnée, que ce soit une clé ou une valeur (mode raw, pas traduit)
   * pour application des filtres
   * @param fei porteur
   * @param line: WidgetLine cible
   * @param code: le code du champ recherché
   * @return une valeur
   */
  public static getRawKeyOrValue(fei: FeiVO, line: WidgetLine, code: string): string { 
    const fkd: FeiKeyDef = this.getKey(fei, code);
    let result: string = null;
    if (fkd && line) {
      if (fkd.isKey) {
        result = this.getRawKey(fei, line, code);   
      } else {
        // prise de la valeur du 1er élément pour les lignes de sous-total dans éléments de filtre des pivots
        if(line.isSubTotalLine) { // prendre la valeur unique, mesure en clé
          let subLine: WidgetLine = line.ch?.size > 0 ? [...line.ch.values()][0] : null;
          if(subLine?.cells?.has(code)) {
            result = this.getValue(fei, subLine, subLine.cells.get(code)); 
          }
        } else {
          if(line.cells?.has(code)) {
            result = this.getValue(fei, line, line.cells.get(code)); 
          }
        }
        
      }

      if(result?.length === 0) {
        result = null;
      }
    }
    return result;
  }

  /** 
   * accesseur sur l'instance du FeiKeyDef d'un FEI
   * @param fei 
   * @param code code#mode si mesure / code#level si key ou filter
   * @returns le FeiKeyDef
   */
  public static getKey(fei: FeiVO, code: string): FeiKeyDef {
    if(fei.keyMap.has(code)) {
      return fei.keyMap.get(code);
    } else {
      // console.log('missing FKD', fei.uniqueName, code);
    }
    
  }

  /**
   * @param fei 
   * @param line: WidgetLine 
   * @param cell: WidgetCell
   * @returns le résultat
   */
  public static getValue(fei: FeiVO, line: WidgetLine, cell: WidgetCell): string {
    let result: string = null;
    if(!!cell) {
      result = fei.model.cellMatrixEngine.getCellValue(line.level, line.key, cell.sc, cell.m);
    }
    return result;
  }

   /**
     * retourne le libellé pour un élément de clé de la widgetLine
     * @param fei 
     * @param line 
     * @param code du FD
     * @param field champ sur le FDV (pour retourner la valeur d'un champ autre que 'label')
     * @returns 
     */
   public static getLabel(fei: FeiVO, line: WidgetLine, code: string, field: string): string {
    if(code === WizConstants.SCEN_KEY_CODE) {
      // TODO TODO gérer les appels depuis scenarEngine
      return line.scen;
    }
    let cell: AkmFDVCell = this.checkFDVCell(fei, line.bl, code, field);
    if(field === WizConstants.FIELD_LABEL) {
      return cell?.label;
    } else if(field === WizConstants.FIELD_CODE) {
      return cell?.rawVal;
    }
     else {
      if(!!cell && !!cell[field]) {
        return cell[field].l;
      }
      return '';  // existe pas  
    }
  }

  /**
   * retourne la valeur brute pour une clé (le label pour le champ du FDV)
   * @param fei 
   * @param wl: WidgetLine
   * @param code du champ
   * @returns 
   */
    public static getRawKey(fei: FeiVO, wl: WidgetLine, code :string): string {   
      if(code === WizConstants.SCEN_KEY_CODE) {
        return wl.scen;
      }
      
      let result: string = null;
      // l'entrée de cellule dans la matrice
      let cell: AkmFDVCell = this.checkFDVCell(fei, wl.bl, code, WizConstants.FIELD_CODE );
      result = cell?.rawVal;
      return result;
    }

  /**
   * retourne la clé de tri pour une valeur de FDV / chronocode, selon le champ
   * @param fei 
   * @param line 
   * @param code du FD
   * @param field champ sur le FDV (pour retourner la valeur d'un champ autre que 'label')
   * @returns 
   */
  public static getSort(fei: FeiVO, line: WidgetLine, code: string, field: string): any {      
    if(code === WizConstants.SCEN_KEY_CODE) {
      return line.scen;
    }
    // console.log('baseline engine getSort');
    let cell: AkmFDVCell = this.checkFDVCell(fei, line.bl, code, field);
    if(!field) {
      field === WizConstants.FIELD_LABEL;
    }
    if(field === WizConstants.FIELD_LABEL) {
      return cell?.sortKey || cell?.label;
    } else {
      if(!!cell && !!cell[field]) {
        return cell[field].s;
      }
      return '';  // existe pas  
    } 
  }

  /**
   * retourne une FDVCell pour un champ sur la baseline
   * va persister le résultat dans la matrice sur l'AkmFDVCell
   * Alimente la rawVal, le label et la sortKey dans tous les cas
   * Alimente prop = value sur AkmFDVCell en cas de propriété autre que le label
   * @param fei 
   * @param line baseline  
   * @param code champ cible (FD / CHRONO)
   * @param field propriété sur le champ. Si null, on utilise le label
   * @returns la cellule, alimentée
   */
  public static checkFDVCell(fei: FeiVO, line: Baseline, code: string, field: string): AkmFDVCell {
      
    let cell: AkmFDVCell = null; // objet retour
    let rawVal: string;
    let fkd: FeiKeyDef = this.getKey(fei, code);

    if(!fkd) {
      return null; // on demande un mauvais champ sur un keyVal, par exemple
    }

    if(fkd.isScen) {  // en fait, on cherche le scenario ...
      cell = fei.model.cellMatrixEngine.getFDVCell(WizConstants.SCEN_KEY_CODE, line.scen, true);
      if(!cell.label) {
        // TODO ici appel sur scenarioEngine pour choper le code scenar
        cell.label = line.scen; // TODO TOO ? ScenarioEngine.getScenario(line.v, line.scen)?.trsLabel || line.scen;
        cell.rawVal = line.scen;
        cell.sortKey = cell.label;
      }
      return cell;
    } 
    
    if(fkd.isKey && !fkd.isFilterKey) { // valeurs de visionKey de la baseline (si nécessaire)
        if(!line.splittedKey) {
            line.splittedKey = line.key.split(WizConstants.KEY_SEPARATOR);
        }
        let fdIndex: number = fkd.indexInKey;
        rawVal = line.splittedKey[fdIndex]; // ma valeur brute pour ce champ


    } else if(fkd.isFilterKey) { // valeurs de filterKey de la baseline
        const fkey: Object = line.fkeyjson || {};
        rawVal = fkey[code];    
    }

    if(!!rawVal) { // appel depuis Keyval, possiblement null
      // on recherche la cellule, on la crée si nécessaire
      cell = fei.model.cellMatrixEngine.getFDVCell(code.split(WizConstants.CONTEXT_SEPARATOR)[0], rawVal, true);

      if(!field) { // si pas de champ, on va prendre le label
        field = WizConstants.FIELD_LABEL;
      }

      if(!cell.rawVal || !cell[field]) { // les valeurs de base ne sont pas alimentés, ou simplement pas le champ demandé
        cell = this.feedFDVCell(fei, code, rawVal, field, cell);
      }
    }
    return cell;
  }

  /**
   * Alimentation d'une AkmFDVCell à partir du couple FD#level / fdv.code ou CHRONO#level / chronoCode
   * @param fei 
   * @param mainKey clé chrono ou FD
   * @param rawValue valeur chrono ou fdv
   * @param field champ à alimenter
   * @param cell AkmFDVCell
   * @returns cell correctement alimentée
   */
  public static feedFDVCell(fei: FeiVO, mainKey: string, rawValue: string, field: string, cell: AkmFDVCell): AkmFDVCell {
      
    if (!mainKey || !rawValue) {
          return cell;
      }
      const kindKey = mainKey.split(WizConstants.CONTEXT_SEPARATOR)[0];
      let label: string = rawValue;
      let sortKey: any = rawValue;
      let pair: KeyPair = null;
      if (kindKey === WizConstants.CHRONO) { // chronocode
          const chrono: ChronocodeVO = fei.model.chronocodeEngine.getChronocode(rawValue);
          if (chrono) {
              label = chrono.trsLabel; // ChronocodeEngine.getChronoValue(chrono, WizConstants.FIELD_LABEL);
              sortKey = chrono.startDate;

              if(!!field && field !== WizConstants.FIELD_LABEL) {
                let customKey: string = fei.model.chronocodeEngine.getChronoValue(chrono, field);
                pair = {r: rawValue, l: customKey, s: sortKey};
                // console.log('chrono customKey', field, customKey);
              }
          }
      } else { // freedata
          const fdv = fei.model.freedataEngine.getFreedataValue(mainKey, rawValue); // lien avec le FDV. l'engine se charge de supprimer l'éventuel #level
          if (fdv) {
              label = fei.model.freedataEngine.getFDValue(fdv, WizConstants.FIELD_LABEL);
              sortKey = label; // label par défaut
              if(!!fdv.order) { // sauf si order
                  // console.log('fdv order', fdv.code, fdv.order);
                  sortKey = fdv.order;
                }
              if(!!field && field !== WizConstants.FIELD_LABEL && field !== WizConstants.FIELD_CODE) {
                let customKey: string = fei.model.freedataEngine.getFDValue(fdv, field);
                pair = {r: rawValue, l: customKey, s: customKey};
                sortKey = customKey; // on écrase la sort par un champ custom
                // if(field === 'SAP_CODE') {
                //   console.log('SAP CODE', fdv, pair);
                // }
                // console.log('FDV customKey', field, customKey);
              }
          }
      }    

      cell.label = label;
      cell.rawVal = rawValue;
      cell.sortKey = sortKey;
      if(!!pair) {
        cell[field] = pair;
      }

      return cell;
  }

  /** 
   * construction de l'ensemble des valeurs de keys / filters Keys pour une baseline (AkmFDVCell)
   * les trios sont alimentés pour le formulaire, pour accès rapide
   * TODO TODO supprimer ça...
   * @param fei 
   * @param baseline 
   * @param force forcer la reconstruction des trios (sur update)
   * @return les trios de la baseline
   */
  public static feedAllKeys(fei: FeiVO, line: Baseline, force: boolean = false): { [key: string]: string } {

    const keyMap: { [key: string]: string } = {};

    // si la clé de ligne n'a pas encore été tronçonnée
    if(!line.splittedKey) { 
      line.splittedKey = line.key.split(WizConstants.KEY_SEPARATOR);
    }

    if(line.trios && !force) { // déjà existant, pas de recréation
      return line.trios;
    }

    // split sur les valeurs de filterKey de la baseline
    const fkey: Object = line.fkeyjson || {};

    // // ajout de la clé pour le scenario
    // alimentation de la clé de scenario
    let scen: string = line.scen;
    keyMap['SCEN@'] = scen; // alimentation de la map pour la BL

    let cell: AkmFDVCell; // pour alimentation des cellules dans la map
    let rawVal: string; // la valeur brute pour une key / filterKey

    for(let i: number = 0; i < fei.visionKeys.length; i++) {
      rawVal = line.splittedKey[i]; // la valeur "brute" (le code de FDV / chrono)
      keyMap[fei.visionKeys[i].fullCode] = rawVal;
      if (!!rawVal && rawVal !== WizConstants.wildcardDomain || rawVal !== WizConstants.err) {
        cell = this.checkFDVCell(fei, line, fei.visionKeys[i].fullCode, rawVal); // alimentation de la cellule dans la matrice
      }
    }

    fei.filterKeys.forEach((fkd: FeiKeyDef) => {
      rawVal = fkey[fkd.fullCode] || null; // la valeur "brut" (le code de FDV / chrono)
      keyMap[fkd.fullCode] = rawVal; // alimentation de la map pour la BL
      if (!!rawVal && rawVal !== WizConstants.wildcardDomain || rawVal !== WizConstants.err) {
        cell = this.checkFDVCell(fei, line, fkd.fullCode, rawVal); // alimentation de la cellule dans la matrice
      }
    });

    line.trios = keyMap; // alimentation de la collection sur la baseline
    return line.trios;
  }

  /**
   * setter de la valeur d'une filterKey
   * @param baseline cible
   * @param code de la clé
   * @param rawVal valeur brute
   */
  public static baselineSetFilterKeyValue(baseline: Baseline, code: string, rawVal: string): void {
    //patch il caste les Object en array, ce con
    if (baseline.fkeyjson instanceof Array || !baseline.fkeyjson) { 
      baseline.fkeyjson = {};
    }
    baseline.fkeyjson[code] = rawVal;
  }

  /**
   * retourne les différences de filterKey pour 2 baselines
   * @param from Bl source
   * @param to BL cible
   * @returns le set des filterKey #
   */
  public static getFilterKeyDelta(from: Baseline, to: Baseline): Set<string> {
    const deltaKey: Set<string> = new Set();
    const existKeys: {} = from.fkeyjson || {};

    Object.keys(existKeys).forEach((key: string) => {
      const existVal: string = existKeys[key];
      const newVal: string = to.fkeyjson[key];
      if (existVal !== newVal) {
        deltaKey.add(key);
      }
    });

    Object.keys(to.fkeyjson || {}).forEach((key: string) => {
      const existVal: string = existKeys[key];
      const newVal: string = to.fkeyjson[key];
      if (existVal !== newVal) {
        deltaKey.add(key);
      }
    });
    return deltaKey;
  }


  /**
   * Mise à jour d'un BLV par programmation.
   * @param baseline cible
   * @param code Le code de la mesure recherchée avec FIST
   * @param valor valeur brute
   * @result la BaselineValue, éventuellement créée
   */
  public static baselineSetValue(baseline: Baseline, code: string, valor: string): BaselineValue {
    if (!baseline.values) {
      baseline.values = {};
    }
    let blv: BaselineValue = baseline.values[code];
    if (!blv) {
      // pas forcément crée, si vide
      blv = this.generateNewBaselineValue(baseline, code);
      baseline.values[code] = blv;
    }
    blv.valor = valor;
    return blv;
  }

  /** 
   * accesseur composite sur la donnée, que ce soit une clé ou une valeur (mode raw, pas traduit)
   * pour application des filtres
   * EN MODE PIVOT. Va retourner la valeur aggrégée
   * @param fei porteur
   * @param line: WidgetLine cible
   * @param code: le code du champ recherché
   * @param field: le champ sur le FDV, en cas de recherche par expression
   * @return un set de valeurs
   */
  public static getPivotRawKeysOrValues(fei: FeiVO, line: WidgetLine, code: string, field: string): Set<string> { 
    const fkd: FeiKeyDef = this.getKey(fei, code);
    
    // en mode pivot, je dois identifier si le champ est en ligne ou en data

    // en ligne, je peu splitter la clé de la line directement

    // en data, j'ai possiblement des colonnes...
    // je dois contrôler tous les pools

    // filterKey => j'ai la valeur sur une de mes lignes enfant

    // TODO PAS DE Filtre mesures en cas de colonnes ??
    // => ou pas... Mais besoin de calculer toutes les colonnes
    // => déléguer au grid et prendre les rendererMap ??

    let result: Set<string> = new Set();
    if(!fkd) {
      return result;
    }

    let lines :WidgetLine[]; // j'ai des enfants (aggrégat) ou pas
    if(!!line.ch) {
      lines = [...line.ch.values()];
    } else {
      lines = [line];
    }

    let tmpRes: string;
    // marchera pour les filterKeys, pas pour les mesures
    lines.forEach((child: WidgetLine) => {
      if (fkd.isKey) {
        if(!!field) { // on demande un champ précis (le label, par exemple)
          tmpRes = this.getLabel(fei, child, code, field);
        } else {
          tmpRes = this.getRawKey(fei, child, code);
        }
      } else {
        tmpRes = this.getValue(fei, child, child.cells.get(code));
      }

      // if(!tmpRes)  {
      //   console.log('no child ??', child, code, field);
      // }

      if(tmpRes?.length > 0) {
        result.add(tmpRes);
      }

    });

    return result;
  }

  /**
   * constructeur d'une nouvelle WidgetLine pour rendu en pivot (WidgetLine de WidgetLines)
   * @param uniqueKey clé d'unicité dans l'agrégat
   * @param WidgetLine source pour construction
   * @param pivotLevel en cas de création gigogne
   */
  public static buildMasterLine(uniqueKey: string, source: WidgetLine): WidgetLine {
    const result: WidgetLine = 
    {
      // construction de la clé unique, pas celle de la BL
      key: uniqueKey, // correspond à la clé d'aggrégat
      uk : uniqueKey, // uniquement pour les pivot, que des visionKey en ligne
      level : source.level,
      scen : source.scen,

      bl : source.bl, // ajout de la ligne témoin (baseline)
      ch : new Map(),
      // on génère sa clé binaire sur la base de l'ID de la baselineTémoin (pour mémorisation rapide des selects)
      binKey : source.binKey,
      // on la marque comme aggrégat
      isSubTotalLine : true,
      subTotalExpended : true
    }

    // alimentation de la map de BLs
    result.ch.set([source.scen, source.key].join(WizConstants.FD_PARTS_SEPARATOR), source);

    return result;
  }

  /**
   * tentative de saisie d'une valeur sur une baselineValue inexistante
   * Ajout à la Baseline
   * @param bl baseline source
   * @param key measure#mode en cours d'ajout
   * @returns la baselineValue (non persistée à ce stade)
   */
  public static generateNewBaselineValue(bl: Baseline, key: string): BaselineValue {
    const blv: BaselineValue = new BaselineValue();
    blv.bid = bl.id;
    blv.domain = bl.domain;
    blv.businessUnitCode = bl.businessUnitCode;
    blv.chron = bl.chron;
    blv.key = bl.key;
    blv.level = bl.level;
    blv.m = key.split(WizConstants.CONTEXT_SEPARATOR)[0];
    blv.mkey = key;
    blv.scen = bl.scen;
    blv.uid = WizMathUtils.createUUID();
    blv.v = bl.v;
    blv.valor = null;
    blv.vs = bl.vs;
    blv.vsp = bl.vsp;
    blv.weight = 1;
    blv.f = key.split(WizConstants.CONTEXT_SEPARATOR)[1];
    return blv; // bl.valuesVOMap.get(key);
  }

  /**
   * clonage de la BL de base pour créer une variante avec scenario. Appelé par DataEngine.generateUpdate
   * @param bl source
   * @param scen cible
   * @returns nouvelle BL avec le bon scenar
   */
  public static cloneBaselineForNewScenario(bl : Baseline, scen: string): Baseline {
    let result: Baseline = JSON.parse(JSON.stringify(bl));
    result.id = null;
    result.version = 0;
    result.operation = EM_ACTION.CREATE;
    result.values = {};
    result.scen = scen;
    result.trios = {};
    result.splittedKey = null;
    result.revisionNumber = -1;
    return result;
  }

  /**
   * clonage de la BLV de base pour créer une variante avec scenario. Appelé par DataEngine.generateUpdate
   * @param blv source
   * @param scen cible
   * @returns nouvelle avec le bon scenar
   */
  public static cloneBaselineValueForNewScenario(blv : BaselineValue, scen: string): BaselineValue {
    let result: BaselineValue = JSON.parse(JSON.stringify(blv));
    result.id = null;
    result.version = 0;
    result.operation = EM_ACTION.CREATE;
    result.scen = scen;
    result.bid = null;
    return result;
  }

  /**
   * compare 2 baselines et retourne les clés de filtre en écart
   * @param source baseline.fkjson existante
   * @param target filterKeys en demande de merge
   * @returns la collection de filterKeys en écart
   */
  public static checkFilterKeyDelta(source: Object, target: Object): Set<string> {
    let result: Set<string> = new Set();

    const checkMap: Map<string, [string, string]> = new Map();
    Object.keys(source || {}).forEach(key => {
      checkMap.set(key, [source[key], null]);
    })
    
    Object.keys(target || {}).forEach(key => {
      if(!checkMap.has(key)) {
        result.add(key); // la clé n'et pas présente dans la source
      } else {
        checkMap.get(key)[1] = target[key];
      }
    })

    Array.from(checkMap.keys()).forEach(key => {
      if(checkMap.get(key)[0] !== checkMap.get(key)[1]) {
        result.add(key); // delta de valeur
      } 
    })
    return result;
  }

  /* ################################################
      conversions pour le widgetDataService
   ################################################ */

  /**
   * sur retour remote, création de la nouvelle baseline
   * @param lite 
   * @return la BL correspondante
   */
  public static baselineLiteToBaseline(lite: BaselineLite): Baseline {
    let bl: Baseline = new Baseline();
    bl.domain = lite.dom;
    bl.businessUnitCode = lite.bu;
    bl.fkeyjson = lite.fkey;
    bl.id = lite.id;
    bl.key = lite.key;
    bl.level = lite.l;
    bl.pkey = lite.pkey;
    bl.revisionNumber = lite.rn;
    bl.scen = lite.scen;
    bl.v = lite.v;
    bl.version = lite.ver;
    bl.vs = lite.vs;
    bl.vsp = lite.vsp;
    // bl.operation = EM_ACTION.CREATE;
    return bl;
  }

  /**
   * sur retour remote, création de la nouvelle baselineValue
   * @param bl porteuse
   * @param lite 
   * @return la BLV correspondante
   */
  public static baselineValueLiteToBaselineValue(bl: Baseline, lite: BaselineValueLite): BaselineValue {
    let blv: BaselineValue = new BaselineValue();
    blv.domain = bl.domain;
    blv.businessUnitCode = bl.businessUnitCode;
    blv.bid = bl.id;
    blv.id = lite.id;
    blv.key = lite.key;
    blv.level = lite.l;
    blv.m = lite.m;
    blv.f = lite.f;
    blv.mkey = [blv.m, blv.f].join(WizConstants.CONTEXT_SEPARATOR);
    blv.revisionNumber = lite.rn;
    blv.scen = lite.scen;
    blv.v = lite.v;
    blv.version = lite.ver;
    blv.vs = lite.vs;
    blv.vsp = lite.vsp;
    blv.valor = lite.val;
    // blv.operation = EM_ACTION.CREATE;
    return blv;
  }

  /**
   * sur retour composite, cast en lite pour recyclage du widgetDataService
   * @param bl 
   * @return la lite correspondante
   */
  public static baselineToBaselineLite(bl: Baseline): BaselineLite {
    let lite: BaselineLite = new BaselineLite();
    lite.dom = bl.domain;
    lite.bu = bl.businessUnitCode;
    lite.fkey = bl.fkeyjson;
    lite.id = bl.id;
    lite.key = bl.key;
    lite.l = bl.level;
    lite.pkey = bl.pkey;
    lite.rn = bl.revisionNumber;
    lite.scen = bl.scen;
    lite.v = bl.v;
    lite.ver = bl.version;
    lite.vs = bl.vs;
    lite.vsp = bl.vsp;
    lite.op = bl.operation;
    return lite;
  }

  /**
   * sur retour composite, cast en lite pour recyclage du widgetDataService
   * @param blv 
   * @return la lite
   */
  public static baselineValueToBaselineValueLite(blv: BaselineValue): BaselineValueLite {
    let lite: BaselineValueLite = new BaselineValueLite();
    lite.bid = blv.bid;
    lite.id = blv.id;
    lite.key = blv.key;
    lite.l = blv.level;
    lite.m = blv.m;
    lite.f = blv.f;
    lite.rn = blv.revisionNumber;
    lite.scen = blv.scen;
    lite.v = blv.v;
    lite.ver = blv.version;
    lite.vs = blv.vs;
    lite.vsp = blv.vsp;
    lite.op = blv.operation;
    lite.val = blv.valor;
    return lite;
  }

  /* ################################################
      retour d'appel compute, mapping
   ################################################ */


  /**
   * sur retour compute, création de la nouvelle baseline
   * 
   * @param fei 
   * @param iba 
   * @returns la BL correspondante
   */
  public static ibaselineToBaseline(fei: FeiVO, iba: IBaseline): Baseline {
    let bl: Baseline = new Baseline();
    bl.domain = fei.vision.domain;
    bl.businessUnitCode = fei.vision.businessUnitCode;
    bl.fkeyjson = iba.fk || null;
    bl.id = iba.id; // alimenté sur le get dans compute
    bl.key = iba.k;
    bl.fkeyjson = iba.fk;
    bl.level = iba.l;
    bl.pkey = null; // on alimente pas depuis compute
    bl.revisionNumber = -1; // on alimente pas depuis compute
    bl.scen = iba.sc;
    bl.v = fei.vision.code;
    bl.version = iba.ver; // alimenté sur le get dans compute
    bl.vs = null;
    bl.vsp = null;

    if(iba.vs) { // alimentation des values
      bl.values = {};
      [...Object.keys(iba.vs)].forEach(blvKey => {
        this.ibaselineValueToBaselineValue(fei, bl, iba.vs[blvKey]);
      });
    }

    return bl;
  }

  /**
   * retour compute, alimentation des BLVs
   * @param fei 
   * @param bl 
   * @param ibva 
   */
  public static ibaselineValueToBaselineValue(fei: FeiVO, bl: Baseline, ibva: IBaselineValue ): void {
    let blv: BaselineValue = new BaselineValue();
    blv.domain = fei.vision.domain;
    blv.businessUnitCode = fei.vision.businessUnitCode;
    blv.id = ibva.id; // alimenté sur le get dans compute
    blv.bid = ibva.bid;
    blv.key = bl.key;
    blv.level = bl.level;
    blv.revisionNumber = -1; // on alimente pas depuis compute
    blv.mkey = ibva.m;
    let splitKey: string[] = blv.mkey.split(WizConstants.CONTEXT_SEPARATOR);
    blv.m = splitKey[0];
    blv.f = splitKey[1];
    blv.scen = bl.scen;
    blv.v = fei.vision.code;
    blv.version = ibva.ver;
    blv.valor = ibva.v;
    bl.values[blv.mkey] = blv;
  }


}
